/** Stable PostHog event taxonomy shared by the browser instrumentation. */
export const EVENTS = {
  // Auth
  AUTH_LOGIN: "auth_login",
  AUTH_LOGOUT: "auth_logout",
  AUTH_SIGNUP: "auth_signup",

  // Onboarding
  ONBOARDING_STARTED: "onboarding_started",
  ONBOARDING_EMAIL_VERIFIED: "onboarding_email_verified",
  ONBOARDING_USERNAME_SET: "onboarding_username_set",
  ONBOARDING_PLAN_SELECTED: "onboarding_plan_selected",
  ONBOARDING_COMPLETED: "onboarding_completed",

  // Profiles
  PROFILE_CREATED: "profile_created",
  PROFILE_UPDATED: "profile_updated",
  PROFILE_DELETED: "profile_deleted",
  PROFILE_TOGGLED: "profile_toggled",
  PROFILE_LIMIT_HIT: "profile_limit_hit",

  // Community
  COMMUNITY_PAGE_VIEWED: "community_page_viewed",
  COMMUNITY_SUBSCRIBED: "community_subscribed",
  COMMUNITY_UNSUBSCRIBED: "community_unsubscribed",
  SUBSCRIPTION_DELIVERY_CHANGED: "subscription_delivery_changed",

  // Reports
  REPORT_VIEWED: "report_viewed",
  REPORT_AUDIO_PLAYED: "report_audio_played",
  REPORT_EXPORTED: "report_exported",
  REPORT_SHARED: "report_shared",

  // OSINT
  OSINT_MAP_VIEWED: "osint_map_viewed",
  OSINT_THEATER_CHANGED: "osint_theater_changed",
  OSINT_LAYER_TOGGLED: "osint_layer_toggled",
  OSINT_TIME_RANGE_CHANGED: "osint_time_range_changed",
  OSINT_INTEL_BRIEF_REQUESTED: "osint_intel_brief_requested",
  OSINT_COUNTRY_CARD_VIEWED: "osint_country_card_viewed",
  OSINT_ALERT_CREATED: "osint_alert_created",
  OSINT_WATCHLIST_CREATED: "osint_watchlist_created",
  OSINT_SOURCES_VIEWED: "osint_sources_viewed",
  OSINT_FININT_VIEWED: "osint_finint_viewed",
  OSINT_TRACKING_VIEWED: "osint_tracking_viewed",
  OSINT_PREDICTION_MARKET_VIEWED: "osint_prediction_market_viewed",
  OSINT_EXPORT_TRIGGERED: "osint_export_triggered",

  // Chat
  CHAT_THREAD_CREATED: "chat_thread_created",
  CHAT_MESSAGE_SENT: "chat_message_sent",
  CHAT_RESPONSE_FEEDBACK_SUBMITTED: "chat_response_feedback_submitted",
  CHAT_TOOL_USED: "chat_tool_used",
  CHAT_MODEL_CHANGED: "chat_model_changed",
  CHAT_THREAD_DELETED: "chat_thread_deleted",
  COPILOT_OPENED: "copilot_opened",
  COPILOT_UPGRADE_WALL_SHOWN: "copilot_upgrade_wall_shown",
  COPILOT_UPGRADE_WALL_CLICKED: "copilot_upgrade_wall_clicked",

  // News
  NEWS_PAGE_VIEWED: "news_page_viewed",
  NEWS_FEED_CHANGED: "news_feed_changed",
  NEWS_ARTICLE_CLICKED: "news_article_clicked",
  NEWS_RECAP_VIEWED: "news_recap_viewed",
  NEWS_TIMEFRAME_CHANGED: "news_timeframe_changed",

  // Billing
  BILLING_PAGE_VIEWED: "billing_page_viewed",
  BILLING_PLAN_SELECTED: "billing_plan_selected",
  BILLING_CHECKOUT_STARTED: "billing_checkout_started",
  BILLING_CHECKOUT_CANCELLED: "billing_checkout_cancelled",
  BILLING_PORTAL_OPENED: "billing_portal_opened",
  BILLING_INTERVAL_TOGGLED: "billing_interval_toggled",
  UPGRADE_BANNER_SHOWN: "upgrade_banner_shown",
  UPGRADE_BANNER_CLICKED: "upgrade_banner_clicked",
  DOWNGRADE_INITIATED: "downgrade_initiated",

  // Developer
  API_KEY_CREATED: "api_key_created",
  API_KEY_REVOKED: "api_key_revoked",
  API_KEY_REGENERATED: "api_key_regenerated",
  WEBHOOK_CREATED: "webhook_created",
  WEBHOOK_UPDATED: "webhook_updated",
  WEBHOOK_TESTED: "webhook_tested",
  WEBHOOK_DELETED: "webhook_deleted",
  DEVELOPER_PAGE_VIEWED: "developer_page_viewed",

  // Branding & Audio
  BRANDING_TEMPLATE_CREATED: "branding_template_created",
  BRANDING_TEMPLATE_UPDATED: "branding_template_updated",
  BRANDING_TEMPLATE_DELETED: "branding_template_deleted",
  AUDIO_VOICE_PREVIEWED: "audio_voice_previewed",
  AUDIO_VOICE_SELECTED: "audio_voice_selected",
  PRONUNCIATION_DICT_CREATED: "pronunciation_dict_created",
  PRONUNCIATION_DICT_UPDATED: "pronunciation_dict_updated",

  // Settings
  SETTINGS_PAGE_VIEWED: "settings_page_viewed",
  SETTINGS_USERNAME_CHANGED: "settings_username_changed",
  SETTINGS_EMAIL_CHANGED: "settings_email_changed",
  SETTINGS_PHONE_ADDED: "settings_phone_added",
  SETTINGS_PHONE_REMOVED: "settings_phone_removed",
  SETTINGS_AVATAR_UPLOADED: "settings_avatar_uploaded",
  SETTINGS_AVATAR_REMOVED: "settings_avatar_removed",
  THEME_TOGGLED: "theme_toggled",
  LANGUAGE_CHANGED: "language_changed",

  // Workspace
  WORKSPACE_SWITCHED: "workspace_switched",
  WORKSPACE_SETTINGS_VIEWED: "workspace_settings_viewed",
  WORKSPACE_MEMBERS_VIEWED: "workspace_members_viewed",
  WORKSPACE_GOVERNANCE_VIEWED: "workspace_governance_viewed",
  WORKSPACE_CREATE_DIALOG_OPENED: "workspace_create_dialog_opened",
  WORKSPACE_CREATE_SUBMITTED: "workspace_create_submitted",
  WORKSPACE_NAME_UPDATE_SUBMITTED: "workspace_name_update_submitted",
  WORKSPACE_DELETE_DIALOG_OPENED: "workspace_delete_dialog_opened",
  WORKSPACE_DELETE_CONFIRMED: "workspace_delete_confirmed",
  WORKSPACE_MEMBER_INVITE_SUBMITTED: "workspace_member_invite_submitted",
  WORKSPACE_INVITE_LINK_COPIED: "workspace_invite_link_copied",
  WORKSPACE_MEMBER_ROLE_CHANGE_SUBMITTED:
    "workspace_member_role_change_submitted",
  WORKSPACE_MEMBER_REMOVE_CLICKED: "workspace_member_remove_clicked",

  // Navigation
  PAGE_VIEWED: "page_viewed",
  SESSION_STARTED: "session_started",
  APP_DAILY_ACTIVE: "app_daily_active",
  APP_MONTHLY_ACTIVE: "app_monthly_active",
  APP_PAGE_VIEWED: "app_page_viewed",
  APP_ENGAGEMENT: "app_engagement",

  // Feedback email entry points
  EMAIL_FEEDBACK_OPENED: "email_feedback_opened",
  EMAIL_CSAT_SUBMITTED: "email_csat_submitted",

  // Ontology v1 — Incidents
  INCIDENT_VIEWED: "incident_viewed",
  INCIDENT_OBSERVATION_DRILLED: "incident_observation_drilled",
  INCIDENT_GRAPH_OPENED: "incident_graph_opened",
  INCIDENT_TIMELINE_SCRUBBED: "incident_timeline_scrubbed",

  // Ontology v1 — Entities
  ENTITY_VIEWED: "entity_viewed",
  ENTITY_GRAPH_TRAVERSED: "entity_graph_traversed",
  ENTITY_RESOLVED_FROM_ALIAS: "entity_resolved_from_alias",
  ENTITY_DISCOVERY_STARTED: "entity_discovery_started",
  ENTITY_DISCOVERY_COMPLETED: "entity_discovery_completed",
  ENTITY_DISCOVERY_FAILED: "entity_discovery_failed",

  // Ontology v1 — Markets
  MARKET_VIEWED: "market_viewed",
  MARKET_SUBJECT_FOLLOWED: "market_subject_followed",
  Y2_MARKET_CREATED: "y2_market_created",
  Y2_MARKET_DERIVED_AUTO: "y2_market_derived_auto",

  // Ontology v1 — Cyber surface
  CYBER_CVE_DRILLED: "cyber_cve_drilled",
  CYBER_THREAT_ACTOR_VIEWED: "cyber_threat_actor_viewed",
  CYBER_THREATS_LOADED: "cyber_threats_loaded",
  CYBER_THREAT_CARD_CLICKED: "cyber_threat_card_clicked",
  CYBER_INCIDENTS_STATUS_TOGGLED: "cyber_incidents_status_toggled",
  CYBER_CVES_FILTER_APPLIED: "cyber_cves_filter_applied",
  CYBER_CVES_VIEW_TOGGLED: "cyber_cves_view_toggled",

  // Ontology v1 — Provenance / corroboration UX
  PROVENANCE_BADGE_HOVERED: "provenance_badge_hovered",
  CITED_SOURCE_OPENED: "cited_source_opened",

  // Ontology v1 — api/v2
  API_V2_REQUEST: "api_v2_request",

  // Intel panels — section navigation
  INTEL_SECTION_VIEWED: "intel_section_viewed",
  INTEL_LATERAL_TAB_CHANGED: "intel_lateral_tab_changed",

  // Intel panels — FININT
  FININT_KPI_CLICKED: "finint_kpi_clicked",
  FININT_DIMENSION_FILTERED: "finint_dimension_filtered",
  FININT_RTO_VIEW_OPENED: "finint_rto_view_opened",
  FININT_NUCLEAR_DRILLED: "finint_nuclear_drilled",
  FININT_CRUDE_PORT_DRILLED: "finint_crude_port_drilled",

  // Intel panels — Cyber
  CYBER_GHOSTWIRE_SOURCE_FILTERED: "cyber_ghostwire_source_filtered",
  CYBER_AI_RISK_DRILLED: "cyber_ai_risk_drilled",
  CYBER_FEED_VIEWED: "cyber_feed_viewed",
  CYBER_THREATS_FEED_SCROLLED: "cyber_threats_feed_scrolled",

  // Intel panels — Explorer / Investigations
  INVESTIGATION_CREATED: "investigation_created",
  INVESTIGATION_CREATED_FROM_SEED: "investigation_created_from_seed",
  INVESTIGATION_OPENED: "investigation_opened",
  INVESTIGATION_NODE_ADDED: "investigation_node_added",
  INVESTIGATION_GRAPH_EXPANDED: "investigation_graph_expanded",
  INVESTIGATION_SHARED: "investigation_shared",
  INVESTIGATION_SAVED: "investigation_saved",
  INVESTIGATION_URL_PRELOAD: "investigation_url_preload",
  INVESTIGATION_PICKER_OPENED: "investigation_picker_opened",
  INVESTIGATION_ENRICH_STARTED: "investigation_enrich_started",
  INVESTIGATION_ENRICH_COMPLETED: "investigation_enrich_completed",
  INVESTIGATION_ENRICH_FAILED: "investigation_enrich_failed",
  INVESTIGATION_DELETED: "investigation_deleted",
  INVESTIGATION_EDGE_ADDED: "investigation_edge_added",
  INVESTIGATION_LAYOUT_APPLIED: "investigation_layout_applied",
} as const;

export type PostHogEventName = (typeof EVENTS)[keyof typeof EVENTS];
